import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/translationService';

export default function OurUniverse() {
  const { language } = useLanguage();
  const [translatedContent, setTranslatedContent] = useState({});
  const [translating, setTranslating] = useState(false);

  // Contenu original en anglais
  const originalContent = {
    hero: {
      title: "Our Universe",
      subtitle: "Discover the story, values, and commitment that make D4U Naturally a unique brand"
    },
    story: {
      title: "Our Story",
      paragraphs: [
        "Founded in 2018, D4U Naturally was born from a passion for the benefits of nature and a deep respect for ancestral Moroccan traditions.",
        "Our journey began with a simple idea: offer natural beauty products of exceptional quality, while preserving the environment and supporting local communities.",
        "Today, we are proud to offer a complete range of natural care that combines the effectiveness of pure ingredients with the latest scientific innovations."
      ]
    },
    values: {
      title: "Our Values",
      items: [
        {
          title: "Naturalness",
          description: "100% natural products, with no compromise on quality and effectiveness."
        },
        {
          title: "Sustainability",
          description: "Committed to production that respects the environment and local communities."
        },
        {
          title: "Innovation",
          description: "Combining ancestral traditions and scientific innovations for unique products."
        },
        {
          title: "Transparency",
          description: "Clear composition and transparent manufacturing processes."
        }
      ]
    },
    timeline: {
      title: "Our Journey",
      milestones: [
        { year: "2018", event: "Creation of D4U Naturally" },
        { year: "2019", event: "First product collection" },
        { year: "2020", event: "Expansion in Europe" },
        { year: "2021", event: "Organic certification obtained" },
        { year: "2022", event: "+10,000 satisfied customers" },
        { year: "2023", event: "New men's line launched" }
      ]
    },
    commitment: {
      title: "Our Commitment",
      items: [
        {
          title: "Ecological",
          description: "Recyclable packaging and environmentally friendly production processes.",
          icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          )
        },
        {
          title: "Ethical",
          description: "Fair trade and support for women's cooperatives in Morocco.",
          icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )
        },
        {
          title: "Innovation",
          description: "Continuous research to improve the effectiveness of our natural products.",
          icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          )
        }
      ]
    }
  };

  // Traduction du contenu
  useEffect(() => {
    const translateContent = async () => {
      if (language === 'en') {
        setTranslatedContent(originalContent);
        return;
      }

      try {
        setTranslating(true);
        console.log(`🌐 Translating Our Universe: en → ${language}`);

        const translated = { ...originalContent };

        // Traduire le contenu principal
        for (const [sectionKey, sectionValue] of Object.entries(originalContent)) {
          if (typeof sectionValue === 'object') {
            for (const [key, value] of Object.entries(sectionValue)) {
              if (typeof value === 'string') {
                translated[sectionKey][key] = await translateText(value, language, 'en');
              } else if (Array.isArray(value)) {
                if (sectionKey === 'story' && key === 'paragraphs') {
                  translated[sectionKey][key] = await Promise.all(
                    value.map(paragraph => translateText(paragraph, language, 'en'))
                  );
                } else if (sectionKey === 'values' && key === 'items') {
                  translated[sectionKey][key] = await Promise.all(
                    value.map(async (item) => ({
                      ...item,
                      title: await translateText(item.title, language, 'en'),
                      description: await translateText(item.description, language, 'en')
                    }))
                  );
                } else if (sectionKey === 'timeline' && key === 'milestones') {
                  translated[sectionKey][key] = await Promise.all(
                    value.map(async (milestone) => ({
                      ...milestone,
                      event: await translateText(milestone.event, language, 'en')
                    }))
                  );
                } else if (sectionKey === 'commitment' && key === 'items') {
                  translated[sectionKey][key] = await Promise.all(
                    value.map(async (item) => ({
                      ...item,
                      title: await translateText(item.title, language, 'en'),
                      description: await translateText(item.description, language, 'en')
                    }))
                  );
                }
              }
            }
          }
        }

        setTranslatedContent(translated);
        console.log('✅ Our Universe translated successfully');
      } catch (error) {
        console.error('💥 Translation error Our Universe:', error);
        setTranslatedContent(originalContent);
      } finally {
        setTranslating(false);
      }
    };

    translateContent();
  }, [language]);

  // Utiliser le contenu traduit ou original
  const content = translatedContent.hero ? translatedContent : originalContent;

  return (
    <div className="min-h-screen bg-white">
      {/* Overlay de traduction */}
      {translating && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#d0b125]"></div>
            <p className="text-[#2f2a1e] font-light">
              {language === 'fr' ? 'Mise à jour des traductions...' : 
               language === 'ar' ? 'جاري تحديث الترجمات...' : 
               'Updating translations...'}
            </p>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#2f2a1e] to-[#1a1712] text-white py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-serif font-light mb-6 tracking-tight">
            {content.hero?.title || "Our Universe"}
          </h1>
          <p className="text-xl text-amber-100/90 font-light max-w-2xl mx-auto leading-relaxed">
            {content.hero?.subtitle || "Discover the story, values, and commitment that make D4U Naturally a unique brand"}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gradient-to-b from-white to-amber-50/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-serif font-light text-gray-900 mb-8">
                {content.story?.title || "Our Story"}
              </h2>
              {content.story?.paragraphs?.map((paragraph, index) => (
                <p key={index} className="text-lg text-gray-700 leading-relaxed font-light">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/about.jpg" 
                  alt="D4U Naturally story"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  onError={(e) => {
                    e.target.src = "/images/placeholder-about.jpg";
                  }}
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[#d0b125] rounded-2xl -z-10"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-amber-100/50">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-serif font-light text-center mb-16 text-gray-900">
            {content.values?.title || "Our Values"}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {content.values?.items?.map((value, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 border border-amber-200 shadow-lg hover:shadow-2xl transition-all duration-500 hover:translate-y-2"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#d0b125] to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif font-light text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {value.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-serif font-light text-center mb-16 text-gray-900">
            {content.timeline?.title || "Our Journey"}
          </h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 md:left-1/2 transform md:-translate-x-1/2 w-1 bg-gradient-to-b from-[#d0b125] to-amber-300 h-full rounded-full"></div>
            
            {content.timeline?.milestones?.map((milestone, index) => (
              <div key={index} className="relative flex items-center mb-16">
                <div className={`flex-1 md:flex-none md:w-1/2 ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8 md:order-2'}`}>
                  <div className="bg-white p-8 rounded-2xl border border-amber-200 shadow-lg hover:shadow-xl transition-all duration-300">
                    <h3 className="text-3xl font-serif font-light text-[#d0b125] mb-3">
                      {milestone.year}
                    </h3>
                    <p className="text-gray-700 text-lg font-light">
                      {milestone.event}
                    </p>
                  </div>
                </div>
                
                <div className="absolute left-6 md:left-1/2 transform md:-translate-x-1/2 w-6 h-6 bg-[#d0b125] rounded-full border-4 border-white shadow-lg"></div>
                
                <div className={`flex-1 md:w-1/2 ${index % 2 === 0 ? 'md:order-2' : ''}`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-amber-100/30">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-4xl font-serif font-light text-center mb-16 text-gray-900">
            {content.commitment?.title || "Our Commitment"}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.commitment?.items?.map((item, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 text-center border border-amber-200 shadow-lg hover:shadow-2xl transition-all duration-500 hover:translate-y-2 group"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-2xl flex items-center justify-center mx-auto mb-6 text-amber-600 group-hover:from-[#d0b125] group-hover:to-amber-500 group-hover:text-white transition-all duration-500">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-serif font-light text-gray-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-lg">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#2f2a1e] to-[#1a1712] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-serif font-light mb-6">
            {language === 'fr' ? 'Rejoignez Notre Aventure' : 
             language === 'ar' ? 'انضم إلى مغامرتنا' : 
             'Join Our Adventure'}
          </h2>
          <p className="text-xl text-amber-100/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            {language === 'fr' ? 'Découvrez nos produits naturels et faites partie de notre communauté engagée.' : 
             language === 'ar' ? 'اكتشف منتجاتنا الطبيعية وكن جزءًا من مجتمعنا الملتزم.' : 
             'Discover our natural products and become part of our committed community.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-[#d0b125] text-white px-8 py-4 rounded-full hover:bg-amber-600 transition-colors duration-300 text-lg font-medium shadow-lg hover:shadow-xl">
              {language === 'fr' ? 'Découvrir les Produits' : 
               language === 'ar' ? 'اكتشف المنتجات' : 
               'Discover Products'}
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-full hover:bg-white hover:text-[#2f2a1e] transition-colors duration-300 text-lg font-medium">
              {language === 'fr' ? 'En Savoir Plus' : 
               language === 'ar' ? 'اعرف المزيد' : 
               'Learn More'}
            </button>
          </div>
        </div>
      </section>

      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 left-4 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded z-50">
          Language: {language}
        </div>
      )}
    </div>
  );
}